import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";

// Valid media types
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

export interface UploadResult {
  url: string;
  type: "image" | "video";
}

export interface FileUploadOptions {
  /**
   * Upload handler function that receives files and returns URLs
   */
  onUpload: (files: File[]) => Promise<UploadResult[]>;
  /**
   * Called when an error occurs during upload
   */
  onError?: (error: string) => void;
  /**
   * Called when upload starts
   */
  onUploadStart?: (files: File[]) => void;
  /**
   * Allowed image MIME types
   */
  allowedImageTypes?: string[];
  /**
   * Allowed video MIME types
   */
  allowedVideoTypes?: string[];
  /**
   * Max file size in bytes (default 50MB)
   */
  maxFileSize?: number;
}

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fileUpload: {
      /**
       * Upload files and insert them into the editor
       */
      uploadFiles: (files: File[]) => ReturnType;
    };
  }
}

export const FileUpload = Extension.create<FileUploadOptions>({
  name: "fileUpload",

  addOptions() {
    return {
      onUpload: async () => [],
      onError: undefined,
      onUploadStart: undefined,
      allowedImageTypes: ALLOWED_IMAGE_TYPES,
      allowedVideoTypes: ALLOWED_VIDEO_TYPES,
      maxFileSize: MAX_FILE_SIZE,
    };
  },

  addCommands() {
    return {
      uploadFiles:
        (files: File[]) =>
        ({ editor }) => {
          this.options.onUploadStart?.(files);

          // Validate files
          const validFiles: File[] = [];
          const allowedTypes = [
            ...(this.options.allowedImageTypes || []),
            ...(this.options.allowedVideoTypes || []),
          ];

          for (const file of files) {
            // Check file type
            if (!allowedTypes.includes(file.type)) {
              this.options.onError?.(`File type "${file.type}" is not supported`);
              continue;
            }

            // Check file size
            if (file.size > (this.options.maxFileSize || MAX_FILE_SIZE)) {
              const maxSizeMB = Math.round(
                (this.options.maxFileSize || MAX_FILE_SIZE) / (1024 * 1024)
              );
              this.options.onError?.(
                `File "${file.name}" is too large. Maximum size is ${maxSizeMB}MB`
              );
              continue;
            }

            validFiles.push(file);
          }

          if (validFiles.length === 0) {
            return false;
          }

          // Upload files and insert
          this.options
            .onUpload(validFiles)
            .then((results) => {
              results.forEach((result) => {
                if (result.type === "video") {
                  editor.chain().focus().setVideo({ src: result.url }).run();
                } else {
                  editor.chain().focus().setImage({ src: result.url }).run();
                }
              });
            })
            .catch((error) => {
              this.options.onError?.(error instanceof Error ? error.message : "Upload failed");
            });

          return true;
        },
    };
  },

  addProseMirrorPlugins() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const extension = this;

    return [
      new Plugin({
        key: new PluginKey("fileUpload"),
        props: {
          handlePaste(view, event) {
            const items = event.clipboardData?.items;
            if (!items) return false;

            const files: File[] = [];

            for (let i = 0; i < items.length; i++) {
              const item = items[i];
              if (item.kind === "file") {
                const file = item.getAsFile();
                if (file) {
                  files.push(file);
                }
              }
            }

            if (files.length === 0) {
              return false;
            }

            // Prevent default paste behavior
            event.preventDefault();

            // Use the uploadFiles command
            const { state, dispatch } = view;
            const tr = state.tr;
            dispatch(tr);

            // Call the upload command via the editor
            extension.editor?.commands.uploadFiles(files);

            return true;
          },

          handleDrop(_view, event) {
            const files = event.dataTransfer?.files;
            if (!files || files.length === 0) return false;

            // Check if any files are media
            const allowedTypes = [
              ...(extension.options.allowedImageTypes || []),
              ...(extension.options.allowedVideoTypes || []),
            ];

            const mediaFiles: File[] = [];
            for (let i = 0; i < files.length; i++) {
              if (allowedTypes.includes(files[i].type)) {
                mediaFiles.push(files[i]);
              }
            }

            if (mediaFiles.length === 0) {
              return false;
            }

            // Prevent default drop behavior
            event.preventDefault();

            // Upload the files
            extension.editor?.commands.uploadFiles(mediaFiles);

            return true;
          },
        },
      }),
    ];
  },
});
